import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { FETCH_BONUS_PROGRESS, clearBonusProgressCache } from "../../api/group";
import { BONUS_CONFIG_SAVED_EVENT } from "../../api/bonusConfigStore";
import { UseSelectedWorkspace, UseCompanyWorkspaces } from "../../hooks/company";
import { UseUserDetails } from "../../hooks/auth";
import { withTeamAverage, realReps, isAverageRep, isLiveMoneyRep } from "../../helpers/teamAverage";
import { mergeMultiWorkspaceBonus } from "../../helpers/bonusRollup";
import { resolveCardWindow } from "../../helpers/cardWindow";
import { resolveWindow } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import { listCombinedAppliesRanges } from "../SalesBonus/moneyRules";
import CardBusy from "./CardBusy";
import { useSelector } from "react-redux";
import "./cardScope.css";

/**
 * Anticipated earnings and Milestone bonuses, over a chosen window.
 *
 * Same Match/Follow-dashboard rule as every other card (see cardWindow.js).
 * Always sends { from, to } so the server can align ladder progress with the
 * same window the metric tiles use.
 *
 * When Anticipated earnings Setup pins rules to Applies-to date ranges, those
 * windows are fetched too and stamped onto each rep as `appliesAmounts` so the
 * breakdown can price Q1–Q2 and Q3 lines separately.
 */

const stampAppliesAmounts = (reps, rangeResults) => {
  if (!rangeResults.length) return reps;
  return (reps || []).map((rep) => {
    const appliesAmounts = { ...(rep.appliesAmounts || {}) };
    rangeResults.forEach(({ key, byOwner }) => {
      const oid = rep.ownerId != null ? String(rep.ownerId) : "";
      appliesAmounts[key] = { ...(byOwner[oid] || {}) };
    });
    return { ...rep, appliesAmounts };
  });
};

/**
 * Per Applies-to range: amounts keyed by owner, optionally scoped to one
 * workspace so multi-ws flatMapped twins keep only their own KPI bag (team
 * average unions keys across twins — merging both KPIs onto each twin would
 * double-count in Team total mode).
 */
const amountsByOwnerFromPairs = (pairs, onlyWsId = null) => {
  const byOwner = {};
  pairs.forEach(([wsId, res]) => {
    if (onlyWsId != null && String(wsId) !== String(onlyWsId)) return;
    realReps(res && res.reps).forEach((r) => {
      const oid = r.ownerId != null ? String(r.ownerId) : "";
      if (!oid) return;
      byOwner[oid] = { ...(byOwner[oid] || {}), ...(r.amounts || {}) };
    });
  });
  return byOwner;
};

const ScopedBonusCard = ({
  settings,
  workspaceId,
  fallback,
  globalRange,
  boardWindow,
  boardCustom,
  boardWs,
  boardWorkspaces,
  children,
}) => {
  const selected = UseSelectedWorkspace();
  const me = UseUserDetails();
  const allWs = UseCompanyWorkspaces(true);
  const usersDict = useSelector((s) => s.companyReducer.usersDictionary) || {};
  const ownWs = workspaceId || (selected && selected._id ? String(selected._id) : null);

  const liveRep = (r) => isLiveMoneyRep(usersDict, r);

  const scoped = (settings.workspaceIds || []).filter(Boolean).map(String);
  const board = (boardWorkspaces || []).filter(Boolean).map(String);
  const wsList = useMemo(() => {
    if (boardWs && boardWs !== "all") return [boardWs];
    if (scoped.length) return scoped;
    if (board.length >= 2) return board;
    return ownWs ? [ownWs] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardWs, scoped.join("|"), board.join("|"), ownWs]);
  const wsKey = wsList.join("|");

  const boardTime = (() => {
    if (boardWindow === "custom" || (boardCustom && (boardCustom.from || boardCustom.to))
      || (globalRange && (globalRange.from || globalRange.to))) {
      return { window: "custom", custom: boardCustom || globalRange || null };
    }
    if (boardWindow) return { window: boardWindow, custom: null };
    /* No navbar at all → null, NOT "month": the board window now outranks the
       card's own Date range (see resolveCardWindow), so forcing "month" here
       would silently veto a pinned card on a board that has no filter. */
    return { window: null, custom: null };
  })();
  const { window: win, custom } = resolveCardWindow(settings, boardTime);
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";

  const programsKey = JSON.stringify(settings.bonusPrograms || {});
  const appliesRanges = useMemo(
    () => listCombinedAppliesRanges(settings.combinedPay),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(settings.combinedPay || null)],
  );
  const appliesKey = appliesRanges.map((r) => r.key).join("|");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pick = win;

  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const onSaved = (e) => {
      const id = e && e.detail && e.detail.workspaceId ? String(e.detail.workspaceId) : null;
      /* Workspace Homepage still saves Group.bonusConfig. Skip refetch when this
         Analytics card already pins its own override for that workspace. */
      if (!id || !wsList.includes(id)) return;
      if (settings.bonusPrograms && settings.bonusPrograms[id]) return;
      /* A real save must beat the result cache — drop it so the refetch is fresh. */
      clearBonusProgressCache();
      setReloadTick((t) => t + 1);
    };
    window.addEventListener(BONUS_CONFIG_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(BONUS_CONFIG_SAVED_EVENT, onSaved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsKey, programsKey]);

  useEffect(() => {
    if (!wsList.length) { setData(null); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    const { from, to } = resolveWindow(pick, custom);
    const range = {
      from: from.format("YYYY-MM-DD"),
      to: to.format("YYYY-MM-DD"),
    };
    const wsName = {};
    (allWs || []).forEach((w) => { wsName[String(w._id)] = w.name; });

    const fetchWs = (id, winRange) => {
      const localProgram = settings.bonusPrograms && settings.bonusPrograms[id]
        ? settings.bonusPrograms[id]
        : null;
      return FETCH_BONUS_PROGRESS(id, winRange, localProgram ? { program: localProgram } : {})
        .then((res) => [id, res]).catch(() => [id, null]);
    };

    /* Setup fires this on every edit (window / program / applies-range change).
       Debounce so dragging a date picker or toggling rules collapses into one
       round of fetches instead of a burst of heavy revenue roll-ups. */
    const run = () => Promise.all([
      Promise.all(wsList.map((id) => fetchWs(id, range))),
      Promise.all(
        appliesRanges.map((ar) => Promise.all(
          wsList.map((id) => fetchWs(id, { from: ar.from, to: ar.to })),
        ).then((pairs) => ({
          key: ar.key,
          pairs: pairs.filter(([, res]) => res),
        }))),
      ),
    ]).then(([basePairs, appliesFetched]) => {
      if (!alive) return;
      const valid = basePairs.filter(([, res]) => res);
      if (!valid.length) { setData(null); setLoading(false); return; }

      const rangeResults = (appliesFetched || [])
        .filter((r) => r && r.pairs && r.pairs.length)
        .map(({ key, pairs }) => ({ key, pairs }));

      if (valid.length === 1) {
        const stamped = {
          ...valid[0][1],
          reps: stampAppliesAmounts(
            realReps(valid[0][1].reps).filter(liveRep),
            rangeResults.map(({ key, pairs }) => ({
              key,
              byOwner: amountsByOwnerFromPairs(pairs),
            })),
          ),
        };
        setData(stamped);
        setLoading(false);
        return;
      }
      const reps = valid.flatMap(([id, r]) => stampAppliesAmounts(
        realReps(r.reps).filter(liveRep).map((rep) => ({ ...rep, wsName: wsName[id] || undefined })),
        rangeResults.map(({ key, pairs }) => ({
          key,
          byOwner: amountsByOwnerFromPairs(pairs, id),
        })),
      ));
      const merged = mergeMultiWorkspaceBonus(valid, wsName);
      setData({
        ...merged,
        reps,
        connected: valid.some(([, r]) => r.connected || (r.config && r.config.revenueKpiId)),
      });
      setLoading(false);
    });

    const timer = setTimeout(run, 250);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsKey, pick, customKey, reloadTick, programsKey, appliesKey]);

  const ownSingle = wsList.length === 1 && !!ownWs && wsList[0] === ownWs;
  const hasLocalProgram = !!(wsList.length === 1 && settings.bonusPrograms && settings.bonusPrograms[wsList[0]]);
  /* Do not let Homepage/ComponentCard workspace fallback overwrite a card-local
     Analytics program — that was one path back to "shared setup". */
  /* The host board's `ctx.bonus` fallback belongs to ONE workspace. On a
     cross-workspace card (Head of Sales) it is a different team's payload, so
     falling back to it when the scoped fetch comes back empty printed another
     workspace's players and pay on this card. Only a single-workspace card
     that IS the host may use it. */
  const raw = (data && fallback && ownSingle && fallback.config && !hasLocalProgram)
    ? { ...data, config: { ...(data.config || {}), ...fallback.config } }
    : (data || (ownSingle ? fallback : null) || null);
  const payload = raw
    ? {
      ...raw,
      myId: (me && (me._id || me.id)) || raw.myId || null,
      connected: raw.connected !== undefined
        ? raw.connected
        : !!(raw.config && raw.config.revenueKpiId),
    }
    : null;
  const showMode = settings.avgMode === "total" ? "total" : "avg";
  const rollupOnly = wsList.length > 1;
  const cold = loading && !payload;
  const cleaned = payload
    ? {
      ...payload,
      reps: (payload.reps || []).filter((r) => isAverageRep(r) || liveRep(r)),
    }
    : null;
  return (
    <div className={`cs-wrap${loading && payload ? " cs-is-refreshing" : ""}`}>
      <CardBusy
        active={loading}
        cover={cold}
        label={cold ? "Loading pay data…" : "Updating for date range…"}
      />
      {cold ? (
        <div className="cs-emptyframe" aria-hidden>
          <div className="cs-emptyframe-h" />
          <div className="cs-empty" />
        </div>
      ) : children({
        data: withTeamAverage(cleaned, showMode),
        loading,
        rollupOnly,
        workspaceIds: wsList,
        window: pick,
        custom,
      })}
    </div>
  );
};

ScopedBonusCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  workspaceId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  fallback: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  globalRange: PropTypes.object,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  boardWs: PropTypes.string,
  boardWorkspaces: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.func.isRequired,
};
ScopedBonusCard.defaultProps = {
  settings: {},
  workspaceId: null,
  fallback: null,
  globalRange: null,
  boardWindow: null,
  boardCustom: null,
  boardWs: null,
  boardWorkspaces: [],
};

export default ScopedBonusCard;
